import { useState } from 'react';
import { useMeasure } from 'react-use';
import Image from 'next/image';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Typography, Grid2, Button, Collapse, Stack, Link } from '@mui/material';
import { Rotate } from '@qb/frontend/components/Rotate';
import { clampLinesSx } from '@qb/frontend/utils/clampLinesSx';
import { convertStringToParagraphs } from '@qb/frontend/utils/convertStringToParagraphs';
import { truncateWithEllipsis } from '@qb/frontend/utils/truncateWithEllipsis';
import { getSPSManufacturerURL } from '@/global/utils/spsURLs';
import { PartDisplayDataResponse } from '@/shared/types/controllers/sps/SPSPartControllerTypes';
const SIX_LINES_HEIGHT = 126;
const COLLAPSE_MIN_HEIGHT = SIX_LINES_HEIGHT;
type PartAboutTheManufacturerProps = {
  manufacturer: PartDisplayDataResponse['part']['manufacturer'];
  manufacturerDescription: string | undefined;
};
export const PartAboutTheManufacturer = ({
  manufacturer,
  manufacturerDescription
}: PartAboutTheManufacturerProps) => {
  const [expanded, setExpanded] = useState(false);
  const [ref, {
    height
  }] = useMeasure();
  const isLongText = height >= SIX_LINES_HEIGHT;
  if (!manufacturer || !manufacturerDescription) {
    return null;
  }
  return <Box sx={{
    mt: 8,
    mb: 4
  }} data-sentry-element="Box" data-sentry-component="PartAboutTheManufacturer" data-sentry-source-file="PartAboutTheManufacturer.tsx">
      <Typography variant="h4" sx={{
      mb: 2
    }} data-sentry-element="Typography" data-sentry-source-file="PartAboutTheManufacturer.tsx">
        About the manufacturer
      </Typography>
      <Grid2 container spacing={3} data-sentry-element="Grid2" data-sentry-source-file="PartAboutTheManufacturer.tsx">
        <Grid2 size={{
        xs: 12,
        md: 2
      }} data-sentry-element="Grid2" data-sentry-source-file="PartAboutTheManufacturer.tsx">
          {!!manufacturer.logoURL && <Link href={getSPSManufacturerURL(manufacturer)} title={`Search ${manufacturer.name} Parts`}>
              <Image src={manufacturer.logoURL} alt={manufacturer.name} title={manufacturer.name} width={300} height={200} style={{
            objectFit: 'contain',
            maxWidth: '100%',
            height: 'auto'
          }} />
            </Link>}
        </Grid2>
        <Grid2 size={{
        xs: 12,
        md: 10
      }} data-sentry-element="Grid2" data-sentry-source-file="PartAboutTheManufacturer.tsx">
          <Collapse in={expanded} collapsedSize={COLLAPSE_MIN_HEIGHT} sx={{
          bgcolor: 'common.white'
        }} data-sentry-element="Collapse" data-sentry-source-file="PartAboutTheManufacturer.tsx">
            <Box ref={ref} data-sentry-element="Box" data-sentry-source-file="PartAboutTheManufacturer.tsx">
              <Typography variant="paragraph" sx={{
              whiteSpace: 'pre-line',
              ...(expanded ? {} : clampLinesSx(6))
            }} data-sentry-element="Typography" data-sentry-source-file="PartAboutTheManufacturer.tsx">
                {!!manufacturerDescription && convertStringToParagraphs(truncateWithEllipsis(manufacturerDescription, 400))}
              </Typography>
            </Box>
          </Collapse>
          <Stack direction="row" sx={{
          mt: 2,
          justifyContent: 'center'
        }} data-sentry-element="Stack" data-sentry-source-file="PartAboutTheManufacturer.tsx">
            <Button size="small" variant="text" color="primary" onClick={() => setExpanded(!expanded)} aria-expanded={expanded} aria-label="show more" sx={{
            visibility: isLongText ? 'visible' : 'hidden'
          }} disabled={!isLongText} endIcon={<Rotate isRotated={expanded}>
                  <ExpandMoreIcon />
                </Rotate>} data-sentry-element="Button" data-sentry-source-file="PartAboutTheManufacturer.tsx">
              Read more
            </Button>
          </Stack>
        </Grid2>
      </Grid2>
    </Box>;
};